import Head from 'next/head';
import { memo, useMemo } from 'react';
import { usePage } from '~/templates/pages';
import { getRuntimeConfig } from '~/shared/utils/config';
import { useFrame, sortStoreList } from '~/shared/utils';
import { prettifyUrl } from '~/templates/pages/utils/prettifyUrl';

type StoreSchema = {
    '@type': 'Store';
    '@id': string;
    name: string;
    url: string;
};

type StoreListItem = {
    '@type': 'ListItem';
    position: number;
    item: StoreSchema;
};

type StoresOverviewSchema = {
    '@context': 'https://schema.org';
    '@graph': [
        {
            '@type': 'CollectionPage';
            '@id': string;
            name: string;
            url: string;
            description?: string;
            mainEntity: {
                '@id': string;
            };
        },
        {
            '@type': 'ItemList';
            '@id': string;
            numberOfItems: number;
            itemListElement: StoreListItem[];
        },
    ];
};

const StoresOverviewSchema = () => {
    const { data, meta, id } = usePage();
    const { config } = getRuntimeConfig();
    const { data: frame } = useFrame();

    const innerHTML = useMemo(() => {
        const stores = sortStoreList(data?.storeList?.data ?? []);

        if (!stores.length) {
            return undefined;
        }

        const storesUrl = frame?.settings?.pageReferences.storesPage?.url;

        if (!storesUrl) {
            return undefined;
        }

        const baseUrl = config.NEXT_PUBLIC_URL;
        const pageUrl = `${baseUrl}${meta?.canonical?.url || ''}`;
        const collectionPageId = `${id}#collectionpage`;
        const itemListId = `${pageUrl}#store-list`;

        const itemListElement: StoreListItem[] = stores
            .filter((store) => store?.name)
            .map((store, index) => {
                const storeUrl = `${baseUrl}${storesUrl}/${prettifyUrl(store.name)}`;
                const storeId = `${store.id}#store`;

                return {
                    '@type': 'ListItem',
                    position: index + 1,
                    item: {
                        '@type': 'Store',
                        '@id': storeId,
                        name: store.name,
                        url: storeUrl,
                    },
                };
            });

        if (!itemListElement.length) {
            return undefined;
        }

        const schemaObject: StoresOverviewSchema = {
            '@context': 'https://schema.org',
            '@graph': [
                {
                    '@type': 'CollectionPage',
                    '@id': collectionPageId,
                    name: meta?.title || 'Stores',
                    url: pageUrl,
                    ...(meta?.description
                        ? {
                              description: meta.description,
                          }
                        : {}),
                    mainEntity: {
                        '@id': itemListId,
                    },
                },
                {
                    '@type': 'ItemList',
                    '@id': itemListId,
                    numberOfItems: itemListElement.length,
                    itemListElement,
                },
            ],
        };

        return {
            __html: JSON.stringify(schemaObject),
        };
    }, [
        data?.storeList?.data,
        frame?.settings?.pageReferences.storesPage?.url,
        config.NEXT_PUBLIC_URL,
        meta?.url,
        meta?.title,
        meta?.description,
    ]);

    if (!innerHTML) {
        return null;
    }

    return (
        <Head>
            <script type="application/ld+json" dangerouslySetInnerHTML={innerHTML} />
        </Head>
    );
};

export default memo(StoresOverviewSchema);
